import type { WorkerRole } from '../../core/index.js'
import { EXAMPLE_STAFFING_PLAN, type HppdTarget, type MinimumStaffingLine, type ShiftTypeKey, type StaffingPlan } from '../../facilities/index.js'
import type { DemoFacility, DemoWorker } from './demoData.js'
import { personAt } from './demoLargeData.js'
import { TRAINING_FACILITY, TRAINING_WORKER_ROLES } from './trainingCast.js'
import type { DemoScenario } from './demoPlan.js'

/** A short-staffed facility (plan H4) employs about this share of the targets: the release waits and the board carries the rest. */
const SHORT_STAFFED_SHARE = 0.7
/** A struggling facility's added staff score this much lower: more call-outs, more shifts run short. */
const STRUGGLING_SCORE_DROP = 12

/**
 * The HPPD target every demo facility is held to: 3.4 hours a resident a day (RN 0.5, LPN 0.5, CNA 2.4), the figure
 * the user's facility runs at under its state's rule (27 October 2026), split 40/35/25 over day, evening and night
 * as the example target is. Written out rather than spread from the example at load, because the facilities index
 * reaches this file through a cycle of module indexes and the example may not exist yet while this module initialises.
 */
export const DEMO_HPPD_TARGET: HppdTarget = { byRole: { RN: 0.5, LPN: 0.5, CNA: 2.4 }, shiftSplit: { day: 40, evening: 35, night: 25 } }

/**
 * The staffing plan every demo facility keeps: the demo's target rounded to the **nearest** seat, not up. Rounded
 * up, a role that needs two or three hours on a shift of a 17-bed unit became a whole eight-hour seat, and the two
 * units needed 29 seats a day - 5.3 hours a resident, half again the target - so a demo worked at about 4.8 HPPD
 * when a facility under a 3.4 HPPD rule runs at about 3.4 (the user, 27 October 2026). Nearest rounding lets the
 * licensed nurses' fractions fall to the minimum staffing lines below, as a building of this size staffs them.
 */
export function demoStaffingPlan(): StaffingPlan {
  // Read when called, not at load, for the cycle described above.
  return { ...EXAMPLE_STAFFING_PLAN, hppd: DEMO_HPPD_TARGET, rounding: 'nearest' }
}

const SHIFT_TYPES: readonly ShiftTypeKey[] = ['day', 'evening', 'night']

/**
 * The minimum staffing lines every demo facility keeps, the floors the nearest rounding never goes under: a licensed
 * nurse (an LPN) and a CNA on every unit on every shift, and the building's RN on the first unit on days and
 * evenings - the eight hours a day a skilled nursing facility must have one, with the LPNs as charge nurses at
 * night. Together with the plan above, the standard building (27 and 17 residents) requires 21 seats a day - 2 RN,
 * 6 LPN, 13 CNA, 168 hours, 3.8 hours a resident - and a roster that fills nine seats in ten works about 3.4 HPPD.
 */
export function demoMinimumStaffingFor(units: readonly { id: string }[]): MinimumStaffingLine[] {
  const first = units[0]
  const everyUnit = SHIFT_TYPES.flatMap((shiftType): MinimumStaffingLine[] => [
    { unitId: null, shiftType, role: 'LPN', minimum: 1 },
    { unitId: null, shiftType, role: 'CNA', minimum: 1 },
  ])
  const buildingRn: MinimumStaffingLine[] = first ? [
    { unitId: first.id, shiftType: 'day', role: 'RN', minimum: 1 },
    { unitId: first.id, shiftType: 'evening', role: 'RN', minimum: 1 },
  ] : []
  return [...everyUnit, ...buildingRn]
}

/**
 * How many of each role an active demo facility employs (demo history plan H3, D2): enough to staff about nine
 * seats in ten of what its staffing plan requires from the roster. The demo's two units at 27 and 17 residents,
 * under the plan and lines above, need 21 seats a day - 2 RN, 6 LPN, 13 CNA - about 640 a month. A full-timer
 * works 16 to 20 shifts a month, a part-timer and a PRN fewer, so 3 RNs, 10 LPNs and 21 CNAs fill roughly 90%
 * (`staffingTargetsFor` at the standard building), leaving the open board a handful a day: what a working facility
 * looks like, and what one administrator can run.
 */
export const DEMO_ROLE_TARGETS: Readonly<Record<WorkerRole, number>> = { RN: 3, LPN: 10, CNA: 21 }

/** A shift's productive hours under the demo's catalogue: eight. */
const SHIFT_HOURS = 8
/** Shifts a month, on average, a demo worker is given by the run (full-timers 16 to 20, part-timers and PRNs fewer): what a roster is sized by. */
export const SHIFTS_PER_WORKER = 17
/** Days in an average month. */
const DAYS_PER_MONTH = 30.4

/** The floor the demo's lines put under a role on a unit and shift: the highest line that names them. */
function floorFor(lines: readonly MinimumStaffingLine[], unitId: string, shiftType: ShiftTypeKey, role: WorkerRole): number {
  return lines.filter((line) => line.role === role && line.shiftType === shiftType && (line.unitId === null || line.unitId === unitId)).reduce((most, line) => Math.max(most, line.minimum), 0)
}

/**
 * The seats a day the demo's staffing plan requires for units at their planned census: each role's hours (census ×
 * its HPPD × the shift's split) over eight-hour shifts, rounded to the nearest seat, never under the demo's minimum
 * staffing lines - the same arithmetic the API's requirement makes for the demo's configuration (the API's
 * `demo/lib/history-seats.test.ts` proves the numbers at the standard building: 2 RN, 6 LPN, 13 CNA).
 */
export function seatsPerDayFor(units: readonly { id: string; plannedCensus: number }[]): Record<WorkerRole, number> {
  const lines = demoMinimumStaffingFor(units)
  const seats: Record<WorkerRole, number> = { RN: 0, LPN: 0, CNA: 0 }
  for (const unit of units) {
    for (const role of Object.keys(seats) as WorkerRole[]) {
      for (const shiftType of SHIFT_TYPES) {
        const hours = unit.plannedCensus * (DEMO_HPPD_TARGET.byRole[role] ?? 0) * (DEMO_HPPD_TARGET.shiftSplit[shiftType] / 100)
        seats[role] += Math.max(hours > 0 ? Math.round(hours / SHIFT_HOURS) : 0, floorFor(lines, unit.id, shiftType, role))
      }
    }
  }
  return seats
}

/**
 * How many of each role a facility of these units employs to staff `staffingPct`% of what its plan requires from the
 * roster (a custom demo): the month's seats for the role, times the share, over what a worker works in a month.
 * At the standard building (27 and 17 residents) and 90% it gives the standard 3, 10 and 21.
 */
export function staffingTargetsFor(units: readonly { id: string; plannedCensus: number }[], staffingPct: number): Record<WorkerRole, number> {
  const daily = seatsPerDayFor(units)
  const target = (role: WorkerRole): number => (daily[role] === 0 ? 0 : Math.max(1, Math.round((daily[role] * DAYS_PER_MONTH * (staffingPct / 100)) / SHIFTS_PER_WORKER)))
  return { RN: target('RN'), LPN: target('LPN'), CNA: target('CNA') }
}

/**
 * The spread of scores the added staff have, a pass through it per role: mostly Silver and Gold, a few Bronze
 * and the odd Probationary, as a real roster reads. Scores cycle, nudged by position so no two read alike.
 */
const FILL_SCORES = [91, 76, 84, 69, 88, 72, 63, 80, 58, 86, 74, 67, 93, 70, 55, 82, 77, 61, 89, 66, 44, 79] as const
const NUDGES = [0, 2, -1, 3, -2, 1] as const
/** The names start partway through the lists, clear of the large demo's administrators and workers. */
const NAME_OFFSET = 700

function at<T>(items: readonly T[], index: number): T {
  return items[((index % items.length) + items.length) % items.length] as T
}

/** At most this many replacements a facility makes over the history: one every other month, as staff turn over at a steady building. */
const MAX_TURNOVER = 3

/**
 * Staff turnover over the history (demo history plan H3): for each of the last few added staff, the person they
 * replaced - same role, who left a few days before they were hired. Both are on the roster, the leaver with their
 * last day, so retention has its hires and departures and the headcount stays level. None for a one-month span.
 */
function withTurnover(fill: readonly DemoWorker[], facility: Pick<DemoFacility, 'facilityId'>, facilityIndex: number, monthSpan: number): DemoWorker[] {
  const replacements = Math.min(MAX_TURNOVER, Math.floor((monthSpan - 1) / 2), fill.length)
  const hires = fill.slice(fill.length - replacements)
  const kept = fill.slice(0, fill.length - replacements)
  const pairs = hires.flatMap((hire, index): DemoWorker[] => {
    const hiredMonthsAgo = 1 + 2 * index
    const { firstName, lastName } = personAt(NAME_OFFSET + 500 + facilityIndex * 10 + index)
    const leaver: DemoWorker = {
      ...hire,
      workerId: `demo-staff-${facility.facilityId}-left-${String(index + 1).padStart(2, '0')}`,
      name: `${firstName} ${lastName}`,
      score: Math.max(30, hire.score - 12),
      leftDaysAgo: hiredMonthsAgo * 30 + 5,
    }
    return [{ ...hire, hiredMonthsAgo }, leaver]
  })
  return [...kept, ...pairs]
}

/**
 * The staff a facility's roster needs beyond those it has: for each role, as many invented workers as bring it to
 * its target, with ids of their own (`demo-staff-<facility>-NN`), so the fixed roster's ids never move, and the
 * turnover a history of `monthSpan` months shows. Deterministic, so every demo has the same people. `existing` is
 * the roles already on the roster.
 */
export function staffingFill(facility: Pick<DemoFacility, 'facilityId'>, existing: readonly WorkerRole[], facilityIndex: number, monthSpan = 1, scenario: DemoScenario = 'steady', targets: Readonly<Record<WorkerRole, number>> = DEMO_ROLE_TARGETS): DemoWorker[] {
  const fill: DemoWorker[] = []
  const share = scenario === 'short_staffed' ? SHORT_STAFFED_SHARE : 1
  const drop = scenario === 'struggling' ? STRUGGLING_SCORE_DROP : 0
  for (const role of Object.keys(targets) as WorkerRole[]) {
    const missing = Math.max(0, Math.round(targets[role] * share) - existing.filter((have) => have === role).length)
    for (let n = 0; n < missing; n += 1) {
      const position = fill.length
      const score = Math.min(99, Math.max(30, at(FILL_SCORES, position + facilityIndex * 5) + at(NUDGES, position + facilityIndex) - drop))
      const { firstName, lastName } = personAt(NAME_OFFSET + facilityIndex * 60 + position)
      fill.push({
        workerId: `demo-staff-${facility.facilityId}-${String(position + 1).padStart(2, '0')}`,
        facilityId: facility.facilityId,
        name: `${firstName} ${lastName}`,
        role,
        score,
        chartingPct: Math.min(100, Math.round(60 + score * 0.4)),
      })
    }
  }
  return withTurnover(fill, facility, facilityIndex, monthSpan)
}

/**
 * The added staff for each facility with activity, given the fixed workers the plan already puts there (and, at
 * Harbor View, the training cast, whose roster comes with its own rows).
 */
export function staffingFillFor(facilities: readonly DemoFacility[], workers: readonly DemoWorker[], monthSpan = 1, scenarioOf: (facilityId: string) => DemoScenario = () => 'steady', indexOffset = 0, targets: Readonly<Record<WorkerRole, number>> = DEMO_ROLE_TARGETS): DemoWorker[] {
  return facilities.flatMap((facility, index) => {
    const facilityIndex = indexOffset + index
    const roles = [...workers.filter((worker) => worker.facilityId === facility.facilityId).map((worker) => worker.role), ...(facility.facilityId === TRAINING_FACILITY.facilityId ? TRAINING_WORKER_ROLES : [])]
    return staffingFill(facility, roles, facilityIndex, monthSpan, scenarioOf(facility.facilityId), targets)
  })
}
